import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import {
  GitBranch,
  Circle,
  Minus,
  Link2,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Undo2,
  Redo2,
} from 'lucide-react'
import type { MapStyle } from '../../../../types/infrastructure-map'

const MAP_STYLE_ICONS: Record<MapStyle, typeof GitBranch> = {
  tree: GitBranch,
  radial: Circle,
  bus: Minus,
}

interface StyleSelectorProps {
  currentStyle: MapStyle
  onStyleChange: (style: MapStyle) => void
  t: TFunction<'cases'>
}

export function StyleSelector({
  currentStyle,
  onStyleChange,
  t,
}: StyleSelectorProps): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <span className="mr-1 text-xs font-medium text-slate-500 dark:text-slate-400">
        {t('infrastructure.canvas.layout')}
      </span>
      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-700">
        {(Object.keys(MAP_STYLE_ICONS) as MapStyle[]).map((style) => (
          <StyleButton
            key={style}
            style={style}
            active={currentStyle === style}
            onSelect={onStyleChange}
            t={t}
          />
        ))}
      </div>
    </div>
  )
}

interface StyleButtonProps {
  style: MapStyle
  active: boolean
  onSelect: (style: MapStyle) => void
  t: TFunction<'cases'>
}

function StyleButton({ style, active, onSelect, t }: StyleButtonProps): ReactElement {
  const Icon = MAP_STYLE_ICONS[style]
  const activeClass = active
    ? 'bg-white dark:bg-slate-600 text-fuchsia-600 dark:text-fuchsia-400 shadow-sm'
    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
  return (
    <button
      onClick={() => {
        onSelect(style)
      }}
      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-150 ${activeClass}`}
      title={t(`infrastructure.canvas.style.${style}Description`)}
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
      <span className="hidden sm:inline">{t(`infrastructure.canvas.style.${style}`)}</span>
    </button>
  )
}

interface ActionsBarProps {
  isLinkCreationActive: boolean
  onToggleLinkCreation: () => void
  onRecalculateLayout: () => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  t: TFunction<'cases'>
}

export function ActionsBar(props: ActionsBarProps): ReactElement {
  const {
    isLinkCreationActive,
    onToggleLinkCreation,
    onRecalculateLayout,
    canUndo,
    canRedo,
    onUndo,
    onRedo,
    t,
  } = props
  return (
    <div className="flex items-center gap-2">
      <LinkCreationToggle active={isLinkCreationActive} onClick={onToggleLinkCreation} t={t} />
      <RecalculateButton onClick={onRecalculateLayout} t={t} />
      <div className="mx-1 h-6 w-px bg-slate-200 dark:bg-slate-700" />
      <UndoRedoControls canUndo={canUndo} canRedo={canRedo} onUndo={onUndo} onRedo={onRedo} t={t} />
    </div>
  )
}

interface LinkCreationToggleProps {
  active: boolean
  onClick: () => void
  t: TFunction<'cases'>
}

function LinkCreationToggle({ active, onClick, t }: LinkCreationToggleProps): ReactElement {
  const activeClass = active
    ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 ring-2 ring-violet-500'
    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-150 ${activeClass}`}
      title={
        active
          ? t('infrastructure.canvas.cancelLinkCreation')
          : t('infrastructure.canvas.createNewLink')
      }
    >
      <Link2 className="h-4 w-4" strokeWidth={1.5} />
      <span className="hidden sm:inline">
        {active ? t('actions.cancel') : t('infrastructure.canvas.link')}
      </span>
    </button>
  )
}

interface RecalculateButtonProps {
  onClick: () => void
  t: TFunction<'cases'>
}

function RecalculateButton({ onClick, t }: RecalculateButtonProps): ReactElement {
  return (
    <button
      onClick={() => {
        onClick()
      }}
      className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
      title={t('infrastructure.canvas.recalculateLayoutPositions')}
    >
      <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
      <span className="hidden sm:inline">{t('infrastructure.canvas.recalculate')}</span>
    </button>
  )
}

interface UndoRedoControlsProps {
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  t: TFunction<'cases'>
}

function UndoRedoControls({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  t,
}: UndoRedoControlsProps): ReactElement {
  return (
    <div className="flex items-center gap-1">
      <IconButton
        onClick={onUndo}
        disabled={!canUndo}
        title={t('infrastructure.canvas.undoShortcut')}
        Icon={Undo2}
      />
      <IconButton
        onClick={onRedo}
        disabled={!canRedo}
        title={t('infrastructure.canvas.redoShortcut')}
        Icon={Redo2}
      />
    </div>
  )
}

interface IconButtonProps {
  onClick: () => void
  title: string
  Icon: typeof Undo2
  disabled?: boolean
}

function IconButton({ onClick, title, Icon, disabled = false }: IconButtonProps): ReactElement {
  const stateClass = disabled
    ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md p-1.5 transition-colors ${stateClass}`}
      title={title}
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
    </button>
  )
}

interface ZoomControlsProps {
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  t: TFunction<'cases'>
}

export function ZoomControls({
  onZoomIn,
  onZoomOut,
  onResetZoom,
  t,
}: ZoomControlsProps): ReactElement {
  return (
    <div className="flex items-center gap-1">
      <IconButton onClick={onZoomOut} title={t('infrastructure.canvas.zoomOut')} Icon={ZoomOut} />
      <IconButton onClick={onZoomIn} title={t('infrastructure.canvas.zoomIn')} Icon={ZoomIn} />
      <IconButton
        onClick={onResetZoom}
        title={t('infrastructure.canvas.resetZoom')}
        Icon={Maximize2}
      />
    </div>
  )
}
